import { useEffect, useRef, useState } from "react";
import { useGetUser } from "atom/user";
import ReactMarkdown from "react-markdown";
import { formAxios2, jsonAxios } from "components/axios";
import Loading from "components/common/Loading";
import { ReactComponent as Add } from 'assets/chat/add.svg'
import { ReactComponent as Del } from 'assets/chat/del.svg'
import { IFileInfo, IFileUpload, IMessage } from "types/chat";
import { ReactTyped } from "react-typed";

interface IChat {
    chatId?: number;
    getChatList?: () => void
    setChatId: React.Dispatch<React.SetStateAction<number>>
    setImgPop: React.Dispatch<React.SetStateAction<boolean>>
    setImgUrl: React.Dispatch<React.SetStateAction<string>>

}

const Chat = ({ chatId, getChatList, setChatId, setImgPop, setImgUrl }: IChat) => {
    const [context, setContext] = useState<string>('');
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [once, setOnce] = useState<boolean>(false);
    const lastBotMessageRef = useRef<HTMLDivElement | null>(null);
    const user = useGetUser();
    const chatRef = useRef<HTMLDivElement>(null);
    const [isMore, setIsMore] = useState<boolean>(false);
    const [isTop, setIsTop] = useState<boolean>(false);
    const [cursor, setCursor] = useState<number>(0);
    const isAsk = useRef<boolean>(false);
    const [fileArr, setFileArr] = useState<IFileUpload[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [initLoading, setInitLoading] = useState<boolean>(false);
    const [sendLoading, setSendLoading] = useState<boolean>(false);

    // 처음 들어와서 chatId가 있을경우
    useEffect(() => {
        setContext('')
        setCursor(0)
        setIsTop(false)
        isAsk.current = false
        setInitLoading(false)
        setIsMore(false)
        setFileArr([])
        setMessages([]);
        if (chatId && chatId > 0) {
            init(0)
        }
    }, [chatId])

    // 메세지 입력시 해당 메세지 질문으로 감
    useEffect(() => {
        if (messages && isAsk.current) {
            const chatArea = document.getElementById('chatArea');

            if (chatArea) {
                // chatArea 안의 마지막 div 선택
                const lastMessage = chatArea.querySelector('.mb-10') as HTMLElement | null;

                if (lastMessage) {
                    setTimeout(() => {
                        lastMessage.scrollIntoView({ behavior: 'smooth' });
                    }, 400);
                }
            }
            isAsk.current = false
        }
    }, [messages, isAsk])

    useEffect(() => {
        if (cursor > 0 && isMore && isTop) {
            init(cursor)
        }
    }, [cursor, isMore, isTop])

    const handleAddClick = () => {
        fileInputRef.current?.click(); // 숨긴 input 클릭
    };
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const newFiles = Array.from(e.target.files)
            .filter(newFile =>
                // 기존 파일과 이름, 용량이 같은 파일은 제외
                !fileArr.some(
                    existing => existing.file.name === newFile.name &&
                        existing.file.size === newFile.size
                )
            )
            .map(file => ({
                file,
                url: URL.createObjectURL(file)
            }));

        if (newFiles.length > 0) {
            setFileArr(prev => [...prev, ...newFiles]);
        }

        e.target.value = '';
    };

    const scrollBottom = () => {
        if (chatRef.current) {
            lastBotMessageRef.current?.scrollIntoView()
        }
    };

    const onScroll = () => {
        if (chatRef.current) {
            const { scrollTop } = chatRef.current;
            if (scrollTop === 0 && isMore) {
                setIsTop(true)
            }
        }
    };

    const init = (cursor: number) => {
        setIsTop(false)
        !cursor && setInitLoading(true)
        const prevScrollHeight = chatRef.current?.scrollHeight || 0;
        const prevScrollTop = chatRef.current?.scrollTop || 0;

        jsonAxios.get(`chat/${chatId}/messages?cursor=${cursor ? cursor : ''}`).then(res => {
            // console.log(res.data)
            if (res.data.code === 200) {
                const newMessages = res.data.data.reverse().map((item: any) => ({
                    role: item.role,
                    message: item.message,
                    fileInfo: item.fileInfo
                }));
                setCursor(res.data.cursor || 0);

                if (res.data.data.length === 10) {
                    setIsMore(true);
                } else {
                    setIsMore(false);
                }

                setMessages((prev) =>
                    cursor === 0 ? newMessages : [...newMessages, ...prev]
                );

                // 📌 스크롤 보정: 새로운 메시지가 추가된 후, 이전 위치 유지
                if (cursor !== 0) {
                    setTimeout(() => {
                        if (chatRef.current) {
                            const newScrollHeight = chatRef.current.scrollHeight;
                            const heightDiff = newScrollHeight - prevScrollHeight;
                            chatRef.current.scrollTop = prevScrollTop + heightDiff;
                        }
                    }, 0);
                }
            } else {
                setMessages([]);
            }
        })
            .finally(() => {
                setTimeout(() => {
                    setInitLoading(false)
                    setTimeout(() => {
                        if (cursor === 0) {
                            scrollBottom();
                        }
                    })
                });
            });
    }

    const send = async () => {
        if (!context || once) return
        setOnce(true)
        setSendLoading(true)
        isAsk.current = true

        try {
            let imgList: IFileInfo[] = [];
            if (fileArr.length > 0) {
                imgList = fileArr.map((file) => ({
                    filePath: file.url,
                    fileName: file.file.name,
                    type: 'img'
                }));
            }
            const newMessages = [
                {
                    role: 'user',
                    message: context,
                    fileInfo: imgList.length > 0 ? imgList : []
                },
            ];
            setMessages((prev: any) => [...prev, ...newMessages]);
            (chatId && chatId > 0) ?
                sendReply() :
                sendMessage();
        } catch (error) {
            console.error('API 오류:', error);
            setOnce(false)
        }
    };

    const sendMessage = () => {
        if (!context) return

        const formData = new FormData();

        formData.append('roomId', String(chatId))
        formData.append('userMessage', context)

        if (fileArr.length > 0) {
            fileArr.forEach((file) => (
                formData.append('files', file.file)
            ))
        }

        formAxios2.post('chat/start', formData).then(res => {
            if (res.data.code === 200) {
                const newMessages = [
                    {
                        role: 'model',
                        message: res.data.data.message,
                    },
                ];

                setMessages((prev: any) => [...prev, ...newMessages]);
                setContext('')
                getChatList?.()
                setChatId?.(res.data.data.roomId)
            }
        }).finally(() => {
            setOnce(false)
            setSendLoading(false);
            setFileArr([])
        })
    }

    const sendReply = () => {
        if (!context) return

        const formData = new FormData();

        formData.append('roomId', String(chatId))
        formData.append('userMessage', context)

        if (fileArr.length > 0) {
            fileArr.forEach((file) => (
                formData.append('files', file.file)
            ))
        }

        formAxios2.post('chat/reply', formData).then(res => {
            if (res.data.code === 200) {
                const newMessages = [
                    {
                        role: 'model',
                        message: res.data.data.message,
                        isNew: true
                    },
                ];

                setMessages((prev: any) => [...prev, ...newMessages]);
                setContext('')
                setFileArr([])
            }
        }).finally(() => {
            // setOnce(false)
            setSendLoading(false);
            setFileArr([])
        })
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    if (!user.id) return null

    const MessageComponent = ({ m, isLastBot, lastBotMessageRef }: any) => {
        const [typedCompleted, setTypedCompleted] = useState(!m.isNew);

        const handleComplete = () => {
            setTypedCompleted(true);

            // isNew 제거
            setMessages((prev: any) =>
                prev.map((msg: any) =>
                    msg === m ? { ...msg, isNew: false } : msg
                )
            );

            // 스크롤 맨 아래
            chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
            setOnce(false)
        };

        return (
            <div
                ref={isLastBot ? lastBotMessageRef : null}
                className={`text-lg ${m.role === "user" ? "bg-primary text-white rounded-4xl" : "bg-transparent text-white"} rounded p-4 max-w-9/10`}
                style={{ whiteSpace: "pre-wrap" }}
            >
                {!typedCompleted && m.isNew ? (
                    <ReactTyped
                        strings={[m.message]}
                        typeSpeed={5}
                        showCursor={false}
                        onComplete={() => handleComplete()}
                        onStringTyped={() => {
                            chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
                        }}
                    />
                ) : (
                    <ReactMarkdown
                        components={{
                            code({ node, className, children, ...props }) {
                                const match = /language-(\w+)/.exec(className || "");
                                return match ? (
                                    <pre className="bg-black my-3 p-3 rounded text-white">
                                        <code className="text-sm" {...props}>
                                            {children}
                                        </code>
                                    </pre>
                                ) : (
                                    <code className="px-1 rounded text-red text-sm" {...props}>
                                        {children}
                                    </code>
                                );
                            },
                        }}
                    >
                        {m.message}
                    </ReactMarkdown>
                )}
            </div>
        );
    }

    const imgPopOpen = (info: IFileInfo) => {
        setImgPop(true)
        setImgUrl(info.filePath)
    }

    return (
        <>
            <div
                id="chatArea"
                className="flex flex-col gap-2 px-4 pb-10 h-dvh overflow-y-scroll text-black"
                ref={chatRef}
                onScroll={() => {
                    onScroll();
                }}
            >
                {
                    initLoading ?
                        <></> :
                        messages.length > 0 ?
                            messages.map((m, idx) => {
                                const isLastBot = m.role === "user" && idx === messages.length - 2;
                                return (
                                    <div
                                        className={`flex w-full ${idx === messages.length - 1 ? 'mb-10' : ''}`}
                                        key={idx}
                                    >
                                        <div className={`flex w-full`}>
                                            <div className={`flex flex-col gap-3 w-full ${m.role === "user" ? "items-end" : "items-start"}`}>
                                                <div className="flex">
                                                    {
                                                        m.fileInfo &&
                                                        m.fileInfo.length > 0 &&
                                                        <div className="flex gap-3">
                                                            {
                                                                m.fileInfo.map((info, idx: number) => (
                                                                    <div
                                                                        onClick={() => {
                                                                            imgPopOpen(info)
                                                                        }}
                                                                        className="flex-1" key={idx}>
                                                                        <img
                                                                            className="rounded w-40 h-40 object-cover hover:scale-105 transition-all cursor-pointer"
                                                                            src={info.filePath}
                                                                            alt={String(idx)}
                                                                        />
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                                <MessageComponent m={m} isLastBot={isLastBot} lastBotMessageRef={lastBotMessageRef} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) :
                            <div className="flex justify-center items-center bg-white mx-auto p-4.5 rounded w-full max-w-[800px]">
                                “복잡한 글로벌 물류, AI가 대신 추적하고 예측합니다. <br />
                                SolomonShip AI와 함께라면 배송의 모든 순간이 더 투명하고 똑똑해집니다.”
                            </div>
                }

                {
                    sendLoading &&
                    <div className="flex justify-center items-center w-full h-[50vh] shrink-0">
                        <Loading />
                    </div>
                }
            </div>
            <div className="bottom-4 left-0 fixed flex flex-col justify-center items-center gap-1 w-full">
                {fileArr.length > 0 && (
                    <div className="flex items-center gap-4 bg-[rgba(0,0,0,0.1)] p-2.5 rounded w-full max-w-[800px] h-25 overflow-x-auto">
                        {fileArr.map((item, idx) => {
                            const handleRemove = () => {
                                URL.revokeObjectURL(item.url); // 메모리 해제
                                setFileArr(prev => prev.filter((_, i) => i !== idx));
                            };

                            return (
                                <div key={idx} className="relative flex-shrink-0">
                                    <img
                                        src={item.url}
                                        alt={item.file.name}
                                        className="rounded w-[120px] h-20 object-cover"
                                    />
                                    {!once && (
                                        <Del
                                            onClick={handleRemove}
                                            className="top-1 right-1 absolute cursor-pointer"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={`flex items-center gap-3 text-black w-full max-w-[800px] mx-auto px-6 rounded-full ${once ? 'bg-[#D9D9D9] cursor-not-allowed' : ' bg-white'}`}>
                    <Add className={`${once ? 'cursor-not-allowed' : ' cursor-pointer'}`} onClick={handleAddClick} />
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        disabled={once}
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <div className="flex flex-1 justify-center items-center py-2 h-full">
                        <textarea
                            className={`w-full h-full px-3 focus:outline-none  ${once ? 'cursor-not-allowed' : ''}`}
                            style={{ resize: "none" }}
                            placeholder="무엇이든 물어보세요"
                            onKeyDown={handleKeyDown}
                            onKeyUp={handleKeyUp}
                            name="input"
                            disabled={once}
                            value={context}
                            onChange={(e) => {
                                setContext(e.target.value)
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default Chat